import React, { useEffect, useRef, useState } from "react";
import {
  UploadCloud, CheckCircle2, AlertCircle, FileSpreadsheet,
  Radio, Copy, RefreshCw, Download,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatRelative } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";

const STAGES = ["Validating", "Normalising", "Inserting", "Indexing", "Complete"];
const POLL_MS = 800;

export default function DataImport() {
  const [tab, setTab] = useState("upload"); // 'upload' | 'webhook'

  return (
    <div data-testid="data-import">
      <PageHeader
        title="Data Import"
        breadcrumb="Tools"
        right={
          <div className="glass-card-sm flex items-center p-0.5">
            <button
              className={`px-3 py-1.5 rounded-md text-[12px] ${tab === "upload" ? "bg-white/70" : ""}`}
              onClick={() => setTab("upload")}
              data-testid="tab-upload"
            >
              Upload File
            </button>
            <button
              className={`px-3 py-1.5 rounded-md text-[12px] ${tab === "webhook" ? "bg-white/70" : ""}`}
              onClick={() => setTab("webhook")}
              data-testid="tab-webhook"
            >
              Webhook Log
            </button>
          </div>
        }
      />

      {tab === "upload" ? <UploadTab /> : <WebhookTab />}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Upload tab — real upload → real polling → real persist
 * ──────────────────────────────────────────────────────────────────── */
function UploadTab() {
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const [uploaded, setUploaded] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [stages, setStages] = useState([]);
  const [currentStage, setCurrentStage] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | processing | complete | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleFile = async (f) => {
    if (!f) return;
    setError(null);
    setUploaded(null);
    setUploadId(null);
    setStages([]);
    setStatus("idle");
    setResult(null);
    setCurrentStage(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/import/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(r.data);
      setUploadId(r.data.upload_id);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    }
  };

  const onProcess = async () => {
    if (!uploadId) return;
    setStatus("processing");
    setStages([]);
    setCurrentStage(null);
    setResult(null);
    try {
      await api.post(`/import/process/${uploadId}`);
      // Begin polling
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/import/status/${uploadId}`);
          setStages(r.data.stages || []);
          setCurrentStage(r.data.current_stage);
          if (r.data.status === "complete") {
            setStatus("complete");
            setResult(r.data.result);
            clearInterval(pollRef.current);
          } else if (r.data.status === "error") {
            setStatus("error");
            setError(r.data.error || "Ingestion failed");
            clearInterval(pollRef.current);
          }
        } catch (pollErr) {
          clearInterval(pollRef.current);
          setStatus("error");
          setError("Status poll failed");
        }
      }, POLL_MS);
    } catch (e) {
      setStatus("error");
      setError(e.response?.data?.detail || "Process trigger failed");
    }
  };

  return (
    <>
      {/* Upload zone */}
      <GlassCard className="p-8 mb-5" data-testid="upload-zone">
        <div
          className={`border-2 border-dashed rounded-xl p-10 text-center transition-all ${dragOver ? "scale-[1.01]" : ""}`}
          style={{
            borderColor: dragOver ? "var(--accent)" : "var(--accent-dim-border)",
            background: dragOver ? "var(--accent-dim)" : "rgba(255, 252, 245, 0.4)",
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files[0]);
          }}
        >
          <UploadCloud size={42} style={{ color: "var(--accent)", margin: "0 auto" }} />
          <h3 className="font-display text-[20px] mt-4" style={{ color: "var(--slate-900)" }}>
            Drop your fleet data file here
          </h3>
          <p className="text-[12px] mt-2" style={{ color: "var(--slate-500)" }}>
            Supports .xlsx, .xls, .csv up to 50 MB · EQP's 168-column format auto-maps
          </p>
          <input ref={fileRef} type="file" hidden accept=".xlsx,.xls,.csv"
            onChange={(e) => handleFile(e.target.files[0])} data-testid="file-input" />
          <button onClick={() => fileRef.current.click()} className="btn-copper mt-5" data-testid="select-file">
            Select File
          </button>
        </div>
        {error && (
          <div className="mt-3 status-pill red flex items-center gap-1.5" data-testid="upload-error">
            <AlertCircle size={12} /> {error}
          </div>
        )}
      </GlassCard>

      {uploaded && (
        <>
          {/* Summary */}
          <GlassCard className="p-5 mb-5" data-testid="upload-summary">
            <div className="flex items-center gap-3 mb-2">
              <FileSpreadsheet size={20} style={{ color: "var(--accent)" }} />
              <div className="flex-1">
                <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>{uploaded.filename}</h3>
                <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>
                  <span className="font-mono">{uploaded.total_rows}</span> rows ·{" "}
                  <span className="font-mono">{uploaded.total_columns}</span> columns ·{" "}
                  <span style={{ color: "var(--status-green)" }}>{uploaded.auto_match_count} auto-mapped</span>
                </p>
              </div>
              <button
                className="btn-copper"
                onClick={onProcess}
                disabled={status === "processing"}
                data-testid="process-button"
              >
                {status === "processing" ? "Processing…" : "Process Data"}
              </button>
            </div>
          </GlassCard>

          {/* Column mapping */}
          <GlassCard className="p-5 mb-5" data-testid="column-mapping">
            <h3 className="font-display text-[16px] mb-3" style={{ color: "var(--slate-900)" }}>Column Mapping Preview</h3>
            <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto">
              {uploaded.mapping.map((m, i) => (
                <div key={i} className="glass-card-sm p-3 flex items-center justify-between text-[12px]">
                  <div className="truncate flex-1 pr-3">
                    <div className="font-mono text-[11px]" style={{ color: "var(--slate-500)" }}>SOURCE</div>
                    <div className="truncate" style={{ color: "var(--slate-900)" }} title={m.source}>{m.source}</div>
                  </div>
                  <div>{m.auto_matched
                    ? <CheckCircle2 size={16} style={{ color: "var(--status-green)" }} />
                    : <span className="status-pill amber" style={{ fontSize: "10px" }}>Unmapped</span>}
                  </div>
                  <div className="truncate flex-1 pl-3 text-right">
                    <div className="font-mono text-[11px]" style={{ color: "var(--slate-500)" }}>TARGET</div>
                    <div className="font-mono" style={{ color: m.auto_matched ? "var(--status-green)" : "var(--slate-500)" }}>
                      {m.target || "—"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Data preview */}
          <GlassCard className="p-5 mb-5 overflow-x-auto" data-testid="data-preview">
            <h3 className="font-display text-[16px] mb-3" style={{ color: "var(--slate-900)" }}>Data Preview (first 10 rows)</h3>
            {uploaded.preview.length > 0 && (
              <table className="w-full text-[11px]">
                <thead>
                  <tr style={{ color: "var(--slate-500)" }}>
                    {Object.keys(uploaded.preview[0]).slice(0, 8).map((k) => (
                      <th key={k} className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-medium">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uploaded.preview.map((row, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: "var(--glass-border-btm)" }}>
                      {Object.keys(row).slice(0, 8).map((k) => (
                        <td key={k} className="px-2 py-2 font-mono truncate" style={{ maxWidth: 140 }}>{row[k]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </GlassCard>

          {/* Real ingestion progress — driven by /import/status polling */}
          {(status === "processing" || status === "complete" || status === "error") && (
            <GlassCard variant="accent" className="p-5" data-testid="processing-status">
              <h3 className="font-display text-[16px] mb-4" style={{ color: "var(--slate-900)" }}>Ingestion Progress</h3>
              <div className="space-y-3">
                {STAGES.map((s) => {
                  const seenStage = stages.find((st) => st.stage === s);
                  const done = !!seenStage;
                  const active = currentStage === s && status === "processing";
                  return (
                    <div key={s} className="flex items-center gap-3" data-testid={`stage-${s.toLowerCase()}`}>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono"
                        style={{
                          background: done ? "var(--accent)" : "var(--slate-100)",
                          color: done ? "white" : "var(--slate-500)",
                          animation: active ? "pulseAccent 1.5s infinite" : "none",
                        }}
                      >
                        {done ? <CheckCircle2 size={12} /> : STAGES.indexOf(s) + 1}
                      </div>
                      <span className="text-[13px]" style={{
                        color: done ? "var(--slate-900)" : "var(--slate-500)",
                        fontWeight: active ? 600 : 400,
                      }}>{s}</span>
                      {seenStage?.detail && (
                        <span className="text-[11px] font-mono" style={{ color: "var(--slate-500)" }}>
                          · {seenStage.detail}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {result && (
                <div className="mt-5 grid grid-cols-4 gap-3" data-testid="ingestion-result">
                  <Summary label="Machines upserted" value={result.machines_upserted} />
                  <Summary label="Telematics rows" value={result.telematics_inserted} />
                  <Summary label="Service calls" value={result.service_inserted} />
                  <Summary label="Alerts triggered" value={result.alerts_triggered} />
                </div>
              )}

              {status === "error" && (
                <div className="mt-4 status-pill red flex items-center gap-1.5">
                  <AlertCircle size={12} /> {error}
                </div>
              )}
            </GlassCard>
          )}
        </>
      )}
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Webhook Log tab — connectivity URL + live event log
 * ──────────────────────────────────────────────────────────────────── */
function WebhookTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [samples, setSamples] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const ingestUrl = `${window.location.origin}/api/webhook/ingest`;

  const fetchLog = async () => {
    setLoading(true);
    try {
      const r = await api.get("/webhook/log");
      setEntries(r.data?.entries || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog();
    api.get("/webhook/samples")
      .then((r) => {
        const swapped = (r.data?.samples || []).map((s) => ({
          ...s,
          curl: s.curl.replace("{INGEST_URL}", ingestUrl),
        }));
        setSamples(swapped);
      })
      .catch(() => {});
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ingestUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* clipboard unavailable */ }
  };

  const copyCurl = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1800);
    } catch (e) { /* clipboard unavailable */ }
  };

  // Pure-frontend transform of /webhook/samples → Postman Collection v2.1.0
  const downloadPostman = () => {
    if (!samples.length) return;
    const ingestUrl = `${window.location.origin}/api/webhook/ingest`;
    const urlObj = new URL(ingestUrl);
    const collection = {
      info: {
        name: "Opsora Webhooks",
        description: "One-click signed payloads for every supported source type. HMAC-SHA256 pre-computed against the live WEBHOOK_SECRET.",
        schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json",
      },
      item: samples.map((s) => ({
        name: `Ingest · ${s.source_type}`,
        request: {
          method: "POST",
          header: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Source-Type", value: s.source_type },
            { key: "X-Opsora-Signature", value: s.signature },
          ],
          body: {
            mode: "raw",
            raw: JSON.stringify(s.payload),
            options: { raw: { language: "json" } },
          },
          url: {
            raw: ingestUrl,
            protocol: urlObj.protocol.replace(":", ""),
            host: urlObj.hostname.split("."),
            path: urlObj.pathname.split("/").filter(Boolean),
          },
        },
      })),
    };
    const blob = new Blob([JSON.stringify(collection, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "Opsora_Webhooks.postman_collection.json";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="webhook-tab">
      {/* Endpoint card */}
      <GlassCard className="p-5 mb-5" data-testid="webhook-endpoint-card">
        <div className="flex items-center gap-3 mb-3">
          <Radio size={20} style={{ color: "var(--accent)" }} />
          <div className="flex-1">
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>
              Universal Ingest Endpoint
            </h3>
            <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>
              Share this URL with your telematics, SAP, IoT or CRM partner. Optional
              HMAC-SHA256 signature header for authenticated payloads.
            </p>
          </div>
          <button
            className="btn-ghost flex items-center gap-1.5"
            onClick={fetchLog}
            data-testid="webhook-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
        <div
          className="glass-card-sm flex items-center justify-between p-3 font-mono text-[12px]"
          style={{ background: "var(--accent-dim)" }}
        >
          <span style={{ color: "var(--slate-900)" }} data-testid="webhook-url">
            POST {ingestUrl}
          </span>
          <button
            className="btn-ghost flex items-center gap-1.5"
            onClick={copy}
            data-testid="webhook-copy"
          >
            <Copy size={12} /> {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]" style={{ color: "var(--slate-700)" }}>
          <div><strong>Header:</strong> <span className="font-mono">X-Source-Type</span></div>
          <div><strong>Header:</strong> <span className="font-mono">X-Opsora-Signature</span></div>
          <div><strong>Header:</strong> <span className="font-mono">X-Idempotency-Key</span></div>
          <div><strong>Formats:</strong> generic · opsora · jcb_livelink · mqtt_bridge · sap_pm</div>
          <div><strong>Ping:</strong> <span className="font-mono">GET /api/webhook/ping</span></div>
          <div><strong>Log:</strong> <span className="font-mono">GET /api/webhook/log</span></div>
        </div>
      </GlassCard>

      {/* Sample payloads — ready-to-run curl for each source type */}
      {samples.length > 0 && (
        <GlassCard className="p-5 mb-5" data-testid="webhook-samples">
          <div className="flex items-start gap-3 mb-1">
            <div className="flex-1">
              <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Sample Payloads</h3>
              <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>
                One-click curl examples — HMAC signature pre-computed against the live <span className="font-mono">WEBHOOK_SECRET</span>. Copy any of them into your terminal to verify connectivity.
              </p>
            </div>
            <button
              className="btn-ghost flex items-center gap-1.5 whitespace-nowrap"
              onClick={downloadPostman}
              data-testid="download-postman"
              title="Download Postman v2.1 collection"
            >
              <Download size={13} /> Postman
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 mt-3">
            {samples.map((s, i) => (
              <div key={s.source_type} className="glass-card-sm p-3" data-testid={`sample-${s.source_type}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="status-pill amber" style={{ fontSize: "10px" }}>{s.source_type}</span>
                  <span className="text-[11px] font-mono" style={{ color: "var(--slate-500)" }}>{s.signature.slice(0, 24)}…</span>
                  <span className="flex-1" />
                  <button
                    className="btn-ghost flex items-center gap-1.5"
                    onClick={() => copyCurl(s.curl, i)}
                    data-testid={`sample-copy-${s.source_type}`}
                  >
                    <Copy size={12} /> {copiedIdx === i ? "Copied!" : "Copy curl"}
                  </button>
                </div>
                <pre className="font-mono text-[11px] whitespace-pre-wrap leading-relaxed" style={{ color: "var(--slate-700)", margin: 0 }}>{s.curl}</pre>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Event log */}
      <GlassCard className="p-5" data-testid="webhook-log">
        <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Recent Events</h3>
        <p className="text-[12px] mb-4" style={{ color: "var(--slate-500)" }}>
          Last 50 webhook events received.
        </p>
        {loading ? (
          <div className="text-center py-6 text-[12px]" style={{ color: "var(--slate-500)" }}>
            Loading…
          </div>
        ) : entries.length === 0 ? (
          <div
            className="text-center py-10 text-[13px]"
            style={{ color: "var(--slate-500)" }}
            data-testid="webhook-empty"
          >
            No webhook events yet. Share the ingest URL above with your integration partner to start receiving live data.
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((e, i) => (
              <div
                key={i}
                className="glass-card-sm p-3 flex items-center gap-3 text-[12px]"
                data-testid={`webhook-event-${i}`}
              >
                <span className="status-pill amber" style={{ fontSize: "10px" }}>
                  {e.source_type || "generic"}
                </span>
                <span className="font-mono" style={{ color: "var(--slate-900)" }}>
                  {e.records_received ?? 0} rec
                </span>
                <span className="font-mono" style={{ color: "var(--status-green)" }}>
                  +{e.machines_upserted ?? 0} machines
                </span>
                {e.alerts_triggered > 0 && (
                  <span className="font-mono" style={{ color: "var(--status-red)" }}>
                    +{e.alerts_triggered} alerts
                  </span>
                )}
                {e.error_count > 0 && (
                  <span className="font-mono" style={{ color: "var(--status-red)" }}>
                    {e.error_count} errors
                  </span>
                )}
                <span className="flex-1" />
                <span style={{ color: "var(--slate-500)" }}>
                  {formatRelative(e.received_at)} · {e.ip || "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="glass-card-sm p-3">
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--slate-500)" }}>{label}</div>
      <div className="font-display text-[22px]" style={{ color: "var(--accent)" }}>{value}</div>
    </div>
  );
}
